import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip, 
  Link,
  Avatar,
  Divider,
  Fade
} from '@mui/material';
import { 
  Download, 
  Trash2, 
  ExternalLink, 
  FileText, 
  Link as LinkIcon,
  HardDrive
} from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  dark: "#22404D",
  azur: "#28708B",
  red: "#F65F4E",
  pearl: "#EEF3F4"
};

const ListadoArchivosComunicacion = ({ pedidoId, actualizar }) => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const ObtenerArchivos = async (id) => {
    try {
      const response = await fetch(`${apiUrl}/Archivo/getArchivosDelPedidoPorPedido?idPedido=${id}`);
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const files = await response.json();
      setUploadedFiles(files);
    } catch (error) {
      toast.error('Error al obtener archivos');
    }
  };

  useEffect(() => {
    ObtenerArchivos(pedidoId);
  }, [pedidoId, actualizar, apiUrl]);

  const handleDownload = async (fileName) => {
    try {
      const response = await fetch(`${apiUrl}/Archivo/descargar/${fileName}`);
      if (!response.ok) throw new Error("No se pudo descargar");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Error al descargar el archivo');
    }
  };

  const handleDelete = async (nombreArchivo, urlAzure) => {
    try {
      const response = await fetch(`${apiUrl}/Archivo/BorrarArchivoComunicacion?nombreArchivo=${nombreArchivo}&pedidoId=${pedidoId}&urlAzure=${urlAzure}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error("Error al borrar");
      ObtenerArchivos(pedidoId);
      toast.success('Archivo eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <Paper elevation={0} sx={{ 
      p: 3, 
      borderRadius: '28px', 
      border: `1px solid ${PALETTE.sky}`,
      backgroundColor: '#FFFFFF',
      mt: 2
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Avatar sx={{ bgcolor: PALETTE.pearl, color: PALETTE.teal, width: 40, height: 40 }}>
          <HardDrive size={20} />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 800, color: PALETTE.dark }}>
          Recursos y Entregables
        </Typography>
      </Box>

      {uploadedFiles.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center', bgcolor: '#fafafa', borderRadius: '20px', border: '1px dashed #ccc' }}>
          <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
            No hay archivos adjuntos en este pedido.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {uploadedFiles.map((file, index) => (
            <Fade in={true} key={index}>
              <Box sx={{ 
                p: 2, 
                borderRadius: '18px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                transition: '0.3s',
                backgroundColor: 'white',
                border: '1px solid #f0f0f0',
                '&:hover': { 
                  borderColor: PALETTE.aqua, 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  transform: 'translateX(5px)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, minWidth: 0 }}>
                  <Avatar sx={{ 
                    bgcolor: file.link ? PALETTE.ice : PALETTE.pearl, 
                    color: file.link ? PALETTE.azur : PALETTE.teal,
                    width: 36, height: 36
                  }}>
                    {file.link ? <LinkIcon size={18} /> : <FileText size={18} />}
                  </Avatar>
                  
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 700, 
                      color: PALETTE.dark,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {file.nombreArchivo}
                    </Typography>
                    
                    {file.link && (
                      <Link 
                        href={file.link} 
                        target="_blank" 
                        sx={{ 
                          fontSize: '0.75rem', 
                          color: PALETTE.aqua, 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        Ver enlace externo <ExternalLink size={12} />
                      </Link>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                  <Tooltip title="Descargar">
                    <IconButton 
                      onClick={() => handleDownload(file.nombreArchivo)}
                      sx={{ color: PALETTE.teal, '&:hover': { bgcolor: PALETTE.ice } }}
                    >
                      <Download size={20} />
                    </IconButton>
                  </Tooltip>

                  {localStorage.getItem('rol') === 'coordinador' && (
                    <Tooltip title="Eliminar">
                      <IconButton 
                        onClick={() => handleDelete(file.nombreArchivo, file.urlAzure)}
                        sx={{ color: PALETTE.red, '&:hover': { bgcolor: '#F65F4E15' } }}
                      >
                        <Trash2 size={20} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Fade>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default ListadoArchivosComunicacion;